import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  OnChanges,
  ViewEncapsulation,
  OnDestroy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';

import { CollectorApiService, ControllerApiService } from '@nrwl/test-setup';
import {
  HelpersService,
  LmapService,
  TestMode,
  WindowWalkService,
  LicensesPageService,
  DevicesService,
  AGENT_MODEL,
  TestModeLabel
} from '@nrwl/common-lib';

import {
  ReportsTransformationService,
  TableData
} from '../../../../services/reports.transformation.service';

import { isNil } from 'lodash';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const diagnosisMap = require('../../mocks/report1/diagnosis-map.json');

@Component({
  selector: 'nrwl-truespeed-reports',
  templateUrl: './truespeed.reports.component.html',
  styleUrls: ['./truespeed.reports.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TruespeedReportsComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('pdfPage') element: ElementRef;
  @Input() expertView: boolean;
  @Input() classicReport: boolean;
  @Input() reportName: string;
  @Input() reportStartTime: string;
  @Input() scheduleId: number;
  @Input() runId: number;
  @Input() testName: string;
  @Input() testData: string;
  @Input() onDemandTest: boolean;
  loading: boolean;
  pageHeight: string;
  clientHeight: number;
  resultType: string;
  upstreamOn: boolean;
  downstreamOn: boolean;
  hasClientInfo: boolean;
  requestInfo = {};
  upstreamSummary = {};
  downstreamSummary = {};
  testSummary = {};
  clientInfo = {};
  startDate = null;
  stopDate = null;
  duration: string;
  rtRequestCtr: number;
  rtTotalTime: number;
  windowResults = [];
  windowSummary = [];
  configuration = {};
  //eslint-disable-next-line
  overallConfiguration: any;
  rtResultsRequested: number; // get all at once...max expected is < 8,000
  currentUrl: string;
  logoUrl: string;
  message: string;
  isThinClientTest: boolean;
  thinClientTestinfo = [];
  reportTitle: string;
  testType: string;
  deviceType: string;
  public: boolean;
  testCapabilities: string;
  testpoints = [];
  upstreamDiagnosisMap = {};
  downstreamDiagnosisMap = {};
  diagnosisMessage = {};
  tagging = 'UNTAGGED';
  l1Rate;
  l3Rate;
  l2Rate;
  finishStatus: string;
  startRTRequestTime: Date;
  upstreamWindowResult;
  downstreamWindowResult;
  clientCpuUsage = [];
  clientMemoryUsage = [];
  displayedColumns: string[] = ['header', 'value'];
  testConfigsDataSource;
  testResultsDataSource;
  clientInfoDataSource;
  bulletChartOptions = {};
  runSaturationWindow = false;
  isAgentToJmep = false;
  truespeedTestResults: Array<TableData>;
  dynamicSaturationWindow = false;
  shaId: string;
  serverConfig = [];
  selectedOriginator: any;
  activeLicense: string;
  licenseDetails: any = [];
  throughputInnerTableHeader = `<tr>
  <td class="throughput-inner-table-data">L1</td>
  <td class="throughput-inner-table-data">L2</td>
  <td class="throughput-inner-table-data">L3</td>
  <td class="throughput-inner-table-data">L4</td></tr>`;
  overallVerdict: string;
  private readonly unsubscribe: Subject<void> = new Subject();
  reportmessage: any;
  testMode = null;
  agentModelCheck: any;
  calculateTimeoutId: any;
  rtResultTimeoutId: any;
  isThresholdUnitMbps = false;
  defaultThresholdValue = 90;
  thresholdUpstream = this.defaultThresholdValue;
  thresholdDownstream = this.defaultThresholdValue;
  constructor(
    private readonly helperService: HelpersService,
    private windowWalkService: WindowWalkService,
    private lmapService: LmapService,
    private transformService: ReportsTransformationService,
    private collectorApiService: CollectorApiService,
    private controllerApiService: ControllerApiService,
    private licenseService: LicensesPageService,
    readonly deviceService: DevicesService
  ) {
    this.expertView = false;
    this.loading = false;
    this.resultType = 'FINAL';
    this.upstreamOn = false;
    this.downstreamOn = false;
    this.hasClientInfo = false;
    this.public = false;
    this.rtRequestCtr = 0;
    this.rtTotalTime = 0;
    this.rtResultsRequested = 1000; // get all at once...max expected is < 8,000
    this.currentUrl = window.location.href;
    this.logoUrl = 'images/viavi-logo-purple.png';
    this.isThinClientTest = false;
    this.reportTitle = 'RFC 6349 TrueSpeed Test Report';
    this.testType = null;
    this.deviceType = null;
    this.testCapabilities = '';
  }

  ngOnInit(): void {
    this.init();
  }
  init(): void {
    this.loading = true;
    const getDiagnosis = diagnosisMap;
    this.diagnosisMessage = getDiagnosis.data;
    this.deviceType = this.windowWalkService.getDeviceType();
    this.upstreamDiagnosisMap = this.createDiagnosisMap('upstream');
    this.downstreamDiagnosisMap = this.createDiagnosisMap('downstream');
    this.getLicenseDetails();
    this.getTestConfig();
    this.getServerConfig();
  }

  getRTResults(fromTime?: string): void {
    this.loading = true;
    let reportFromTime = this.reportStartTime;
    this.rtRequestCtr++;
    this.startRTRequestTime = new Date();
    if (typeof fromTime !== 'undefined') {
      reportFromTime = fromTime;
    }
    this.collectorApiService
      .getQueryReportByFromTime(
        this.scheduleId,
        this.runId,
        'rt',
        reportFromTime,
        this.rtResultsRequested
      )
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          if (res.status === 'success') {
            const getRTResultResponse = res.data.filter(data => {
              const instanceStartTime = this.lmapService.getTagValue(
                data['ietf-lmap-report:report'].result[0].tag,
                'instance-start-time'
              );
              return instanceStartTime === this.reportStartTime;
            });
            this.processRTResults(getRTResultResponse);
            this.calculateTotalStat();
          }
        },
        err => {
          //eslint-disable-next-line
          console.log('error on RT data ' + err?.message);
          this.loading = false;
        }
      );
  }
  getLicenseDetails(): void {
    this.licenseService.getLicenseCount().subscribe(response => {
      this.licenseDetails = response;
    });
  }

  setActiveLicense(): void {
    let thinClientobj = null;
    let fatClientobj = null;
    const isQtDevice =
      this.selectedOriginator?.deviceData.model.model.indexOf('QT') !== -1 ||
      this.selectedOriginator?.deviceData.model.model.indexOf('NSC') !== -1 ||
      this.selectedOriginator?.deviceData.model.model.indexOf('MAP-2100') !== -1;
    if (this.licenseDetails.length > 0) {
      thinClientobj = this.licenseDetails.find(o => o.featureName === 'EAX-TRUESPEED-PC-TC');
      fatClientobj = this.licenseDetails.find(o => o.featureName === 'EAX-TS-PC-CLIENT');
      if (thinClientobj && fatClientobj && !isQtDevice) {
        this.activeLicense = 'tf';
      } else if (thinClientobj && !isQtDevice) {
        this.activeLicense = 'thin-engine';
      } else if (fatClientobj) {
        this.activeLicense = 'fat-engine';
      }
    }
  }

  //eslint-disable-next-line
  processRTResults(response) {
    // Check response to make sure it is valid
    const errorMsg = this.helperService.getResponseErrorMsg(response);
    if (errorMsg == null) {
      if (response) {
        let totalRtReports = 0;
        let lastSampleDate = '';
        if (response.length && response.length > 0) {
          totalRtReports = response.length;
          const responseData = {
            data: []
          };
          responseData.data = response;
          lastSampleDate = this.lmapService.getReportPostDate(responseData, response.length - 1);
        }

        // separate the report content into results per window
        const allRtReport = response;

        for (let i = 0; i < allRtReport.length; i++) {
          const report = this.lmapService.ltranslateLmapReportIndexToAngular(allRtReport, null, i);
          if (report[0] && report[0].status.indexOf('ABORTED') !== -1) {
            this.finishStatus = 'Test Aborted';
          }

          if (report[0] && report[0].status.indexOf('COMPLETE') !== -1) {
            this.finishStatus = 'Test Complete';
          }

          if (
            report &&
            report[0] &&
            report[0].status &&
            report[0].status.indexOf('WINDOW') !== -1
          ) {
            let thisWindow = null;
            for (let x = 0; x < this.windowResults.length; x++) {
              if (this.windowResults[x] && this.windowResults[x].status === report[0].status) {
                thisWindow = this.windowResults[x];
              }
            }
            if (thisWindow === null) {
              thisWindow = {
                status: report[0].status,
                id: report[0]['window-id'] || 1,
                seq: [], //track which seq number the data came from to prevent duplicates
                thruput: [],
                rtt: [],
                transmitted: [],
                retransmitted: [],
                retransRatio: []
              };
              // put a starter value in each array since High Charts does not display the first value
              thisWindow.thruput.push(0);
              thisWindow.rtt.push(0);
              thisWindow.transmitted.push(0);
              thisWindow.retransmitted.push(0);

              this.windowResults.push(thisWindow);
            }

            if (report[0]['current-thruput'] !== '') {
              let exists = false;
              for (let z = 0; z < thisWindow.seq.length; z++) {
                if (thisWindow.seq[z] === report[0][this.lmapService.CONST.resultSequenceId]) {
                  exists = true;
                  break;
                }
              }
              if (exists === false) {
                const transmitted = parseFloat(report[0]['current-transmitted-frames']);
                const retransmitted = parseFloat(report[0]['current-retransmitted-frames']);

                thisWindow.id = report[0]['window-id'] || 1; // sometimes empty in initial report so always set it
                thisWindow.seq.push(report[0][this.lmapService.CONST.resultSequenceId]);
                thisWindow.thruput.push(parseFloat(report[0]['current-thruput']));
                thisWindow.rtt.push(parseFloat(report[0]['current-rtt']));
                thisWindow.transmitted.push(transmitted);
                thisWindow.retransmitted.push(retransmitted);

                if (transmitted !== 0) {
                  thisWindow.retransRatio.push((retransmitted / transmitted) * 100);
                } else {
                  thisWindow.retransRatio.push(0);
                }
              }
            }
          }
        }

        if (!this.finishStatus) {
          this.finishStatus = 'Test Aborted';
        }

        const currentTime = new Date();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const elapsedTime = currentTime - this.startRTRequestTime;
        this.rtTotalTime += elapsedTime;
        if (this.rtResultsRequested === totalRtReports) {
          this.rtResultTimeoutId = setTimeout(() => this.getRTResults(lastSampleDate), 100);
        } else {
          // order windowResults so that Window B's results follow Window A's for the same Window test
          this.loading = false;
          let notDone = true;
          while (notDone === true) {
            notDone = false;
            for (let x = 0; x < this.windowResults.length && notDone === false; x = x + 2) {
              if (x + 1 < this.windowResults.length) {
                if (this.windowResults[x].status.indexOf('ENGINE_A') >= 0) {
                  const engineAStatus = this.windowResults[x].status;
                  const engineBStatus = engineAStatus.replace('ENGINE_A', 'ENGINE_B');
                  if (this.windowResults[x + 1].status !== engineBStatus) {
                    // find matching B data and move it after A
                    for (let z = 0; z < this.windowResults.length; z++) {
                      if (this.windowResults[z].status === engineBStatus) {
                        // first remove the item that is later in the array
                        const engineBData = this.windowResults.splice(z, 1);
                        if (engineBData[0]) {
                          // insert it righ after the associated A data
                          this.windowResults.splice(x + 1, 0, engineBData[0]);
                        }
                        //once we have changed the array, start checking from the beginning
                        notDone = true;
                        break;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    } else {
      this.windowResults = [];
    }
  }

  getServerConfig(): void {
    this.controllerApiService.getServerConfig().subscribe(response => {
      this.serverConfig = response.data;
      const serverConfig = response.data['reports'];
      if (serverConfig && serverConfig.logo && serverConfig.logo.url) {
        this.logoUrl = serverConfig.logo.url;
      }
      if (serverConfig && serverConfig.message) {
        this.reportmessage = serverConfig.message;
      }
    });
  }

  getTestConfig(): void {
    this.controllerApiService
      .getTestConfig(this.scheduleId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response.status === 'success') {
          this.overallConfiguration = response.data.config;
          this.shaId = response.data?.sha ? response.data.sha : null;
          this.configuration = this.windowWalkService.parseConfiguration({
            config: this.overallConfiguration
          });
          const result = this.determineAgentMode(response?.data?.config?.agent);
          this.isAgentToJmep = result.isAgentToJmep;
          this.isThresholdUnitMbps = this.configuration?.['thresholdUnitMbps'] || false;
          this.thresholdUpstream = this.configuration?.['threshold'] || this.defaultThresholdValue;
          this.thresholdDownstream =
            this.configuration?.['thresholdDownstream'] || this.defaultThresholdValue;
          this.processReport();
          this.getRTResults();
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  processStartStopDates() {
    function padZero(x) {
      return x < 10 ? '0' + x : x.toString();
    }
    if (this.stopDate && this.startDate) {
      let diffTime = this.stopDate.getTime() - this.startDate.getTime();
      diffTime = diffTime / 1000;

      let minutes = Math.floor(diffTime / 60);
      const seconds = diffTime % 60;
      let hours = 0;
      if (minutes >= 60) {
        hours = Math.floor(minutes / 60);
        minutes = minutes % 60;
      }
      let days = 0;
      if (hours >= 24) {
        days = Math.floor(hours / 24);
        hours = hours % 24;
      }

      this.duration =
        (days ? days + 'd ' : '') +
        (hours ? hours + 'h ' : '') +
        (minutes ? minutes + 'm ' : '') +
        seconds +
        's';
    }
  }

  //eslint-disable-next-line
  getFinalReportBasedOnStartDate(res: any): any {
    if (res.data && res.data.length > 0) {
      if (
        res.data[0]?.['ietf-lmap-report:report'] &&
        res.data[0]?.['ietf-lmap-report:report']?.result
      ) {
        const startDate = res.data[0]['ietf-lmap-report:report'].result[0].start;
        return res.data.filter(
          item => item['ietf-lmap-report:report'].result[0].start === startDate
        );
      }
    }
    return res.data;
  }

  // ToDo return type and parameter can be typed while integrated with backend.
  // eslint-disable-next-line
  processReport(): any {
    this.collectorApiService
      .getQueryReportByFromTime(this.scheduleId, this.runId, 'final', this.reportStartTime, 1000)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        if (res.status === 'success') {
          const getReportResponse = this.getFinalReportBasedOnStartDate(res);
          const latestReport = this.windowWalkService.getLatestReport(getReportResponse);

          // Set start/stop dates
          this.startDate = null;
          this.stopDate = null;

          if (getReportResponse.length) {
            for (const reportData of getReportResponse) {
              const report = reportData['ietf-lmap-report:report'];
              if (report) {
                const myStopDate = new Date(report.date);
                if (!this.stopDate || myStopDate.getTime() > this.stopDate.getTime()) {
                  this.stopDate = myStopDate;
                }

                if (!this.startDate && report.result?.length > 0) {
                  this.startDate = new Date(report.result[0].start);
                }
              }
            }
          }
          this.processStartStopDates();

          //overriding test config with current testid config
          const currentRunConfig = this.getTestOption(getReportResponse);
          this.overallConfiguration.config[0].primaryConfig[
            'ietf-lmap-control:lmap'
          ].schedules.schedule[0].action[0].option = currentRunConfig;
          this.configuration = this.windowWalkService.parseConfiguration({
            config: this.overallConfiguration
          });
          this.selectedOriginator = this.deviceService.findById(
            this.configuration?.['engineA']?.address
          );
          this.runSaturationWindow =
            this.configuration?.['runSaturationWindow'] === 'true' ? true : false;
          this.dynamicSaturationWindow =
            this.configuration?.['dynamicSaturation'] === 'true' ? true : false;
          this.testType = this.isAgentToJmep ? TestMode.MA_TO_JMEP : this.testType;
          if (this.testSummary['tcpPort']) {
            this.configuration['tcpPort'] = this.testSummary['tcpPort'];
          }
          this.requestInfo = this.createRequestInfo(this.overallConfiguration);
          this.setActiveLicense();

          this.processResults(latestReport, this.configuration);
          if (this.requestInfo['mode'] === 'CLIENT-TO-MA') {
            this.processClientInfo(getReportResponse);
          }
        }
      });
  }

  //eslint-disable-next-line
  getTestOption(response): any {
    let optionValue = [];
    if (response.length) {
      const reports = response[0];
      const testOptions = reports['ietf-lmap-report:report'].result[0].option;
      optionValue = testOptions;
    }
    return optionValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  determineAgentMode(agentConfig: any): {
    testMode: TestModeLabel;
    mode: string;
    isAgentToJmep: boolean;
  } {
    let testMode = TestModeLabel.CLIENT_TO_MA;
    let mode = TestMode.CLIENT_TO_MA.toUpperCase();
    let isAgentToJmep = false;

    //Check if jmepIpAddress exists and has a value
    if (this.configuration?.['jmepIpAddress']) {
      testMode = TestModeLabel.MA_TO_JMEP;
      mode = TestMode.MA_TO_JMEP.toUpperCase();
      isAgentToJmep = true;
      return { testMode, mode, isAgentToJmep };
    } else if (agentConfig) {
      if (agentConfig.secondary && agentConfig.secondary.length > 0) {
        const secondary = agentConfig.secondary[0];
        const secondaryId = secondary.id;
        this.agentModelCheck = this.deviceService.findDeviceByUniqueName(secondaryId);

        testMode = TestModeLabel.MA_TO_MA;
        mode = TestMode.MA_TO_MA.toUpperCase();
        isAgentToJmep = false;
      } else if (!agentConfig.secondary) {
        const primary = agentConfig.primary;
        this.agentModelCheck = this.deviceService.findDeviceByUniqueName(primary.id);
        testMode = TestModeLabel.CLIENT_TO_MA;
        mode = TestMode.CLIENT_TO_MA.toUpperCase();
      }
    }

    return { testMode, mode, isAgentToJmep };
  }

  //eslint-disable-next-line
  createRequestInfo(data: any): any {
    const requestInfo = {};
    const agentConfig = this.overallConfiguration?.agent;
    if (agentConfig) {
      const result = this.determineAgentMode(agentConfig);
      this.testMode = result.testMode;
      this.isAgentToJmep = result.isAgentToJmep;
      requestInfo['mode'] = result.mode;
    }

    requestInfo['testName'] =
      data?.config[0]?.primaryConfig['ietf-lmap-control:lmap']?.schedules?.schedule[0]?.name;
    if (data && data.test && data.test['extended-config']) {
      requestInfo['techInfo'] = data.test['extended-config'].technicianInfo;
      requestInfo['testName'] = data.test['extended-config'].testInfo.name;
      requestInfo['mode'] = data.test['extended-config'].test.mode.toUpperCase();
      this.testMode =
        data.test['extended-config'].test.mode === TestMode.CLIENT_TO_MA
          ? TestModeLabel.CLIENT_TO_MA
          : data.test['extended-config'].test.mode === TestMode.MA_TO_JMEP
          ? TestModeLabel.MA_TO_JMEP
          : TestModeLabel.MA_TO_MA;
      requestInfo['comments'] = data.test['extended-config'].testInfo.comments;
    }

    if (data && data.test && data.test['test-list-data']) {
      requestInfo['customerInfo'] = data.test['test-list-data'].customer;
      requestInfo['mode'] = data.test['test-list-data'].mode.toUpperCase();
      this.testMode =
        data.test['test-list-data'].mode === TestMode.CLIENT_TO_MA
          ? TestModeLabel.CLIENT_TO_MA
          : data.test['test-list-data'].mode === TestMode.MA_TO_JMEP
          ? TestModeLabel.MA_TO_JMEP
          : TestModeLabel.MA_TO_MA;
    }
    const primaryUrl = this.helperService.createPublicAccessURL(this.serverConfig, this.shaId);
    const secondaryUrl = this.helperService.generateSecondaryTestURL(
      this.serverConfig,
      this.shaId,
      this.selectedOriginator
    );
    requestInfo['expirationDate'] = this.helperService.getReportDateTimeString(
      data?.test?.controller?.['expiration-date']
    );
    requestInfo['dmzUrl'] = primaryUrl ? primaryUrl + '&type=' + this.activeLicense : '';
    requestInfo['vtaTsUrl'] = secondaryUrl ? secondaryUrl + '&type=' + this.activeLicense : '';
    let agentName = data && data && data.agent && data.agent.primary ? data.agent.primary.id : null;
    if (!agentName) {
      agentName =
        data &&
        data &&
        data.test &&
        data.test['extended-config'] &&
        data.test['extended-config'].testPoints &&
        data.test['extended-config'].testPoints > 0
          ? data.test['extended-config'].testPoints[0].name
          : null;
    }
    return requestInfo;
  }

  //eslint-disable-next-line
  processResults(result: any, configuration: any): any {
    let windowResults = null;
    this.bulletChartOptions = {};
    const tableData = result['ietf-lmap-report:report'].result[0].table[0];
    const resultsArr = result['ietf-lmap-report:report'].result[0];
    const testTypeIndex = tableData.column.indexOf('truespeed-type');
    const tableRowData = tableData.row[tableData.row.length - 1];
    if (this.testType === null) {
      if (tableRowData.value[testTypeIndex] === 'thin-engine') {
        this.testType = tableRowData.value[testTypeIndex];
        this.isThinClientTest = true;
        this.reportTitle = 'TrueSpeed Light Test Report';
      } else {
        this.isThinClientTest = false;
        this.reportTitle = 'TrueSpeed Full Test Report';
      }
    }
    if (!this.isThinClientTest) {
      windowResults = this.windowWalkService.getWindowResults(result, configuration);
    } else {
      windowResults = this.windowWalkService.getThinClientWindowResults(result, configuration);
    }
    const testConfigs = this.transformService.getTrueSpeedTestConfigs(
      this.configuration,
      this.testType, // is thin-engine or fat-engine
      this.requestInfo['mode'].toLowerCase()
    );
    this.testConfigsDataSource = new MatTableDataSource(testConfigs);
    this.upstreamWindowResult = windowResults.upstream;
    this.downstreamWindowResult = windowResults.downstream;
    const summary = this.windowWalkService.getSummary(result, configuration);
    this.upstreamSummary = summary.upstreamSummary;
    this.downstreamSummary = summary.downstreamSummary;

    this.upstreamSummary['diagnosisMessage'] = this.getDiagnosis(
      'upstream',
      this.upstreamSummary['diagnosis']
    );
    this.downstreamSummary['diagnosisMessage'] = this.getDiagnosis(
      'downstream',
      this.downstreamSummary['diagnosis']
    );

    this.testSummary['mtu'] = windowResults.mtu;
    this.testSummary['mss'] = windowResults.mss;
    //this.testSummary['rtt'] = parseFloat(windowResults.rtt).toFixed(2);
    this.testSummary['thinClientTcpPort'] = windowResults.tcpPort;
    this.testSummary['rttBase'] = parseFloat(windowResults.rttBase).toFixed(2);
    this.testSummary['httpsRttAvg'] = parseFloat(windowResults.httpsRttAvg).toFixed(2);
    this.testSummary['thinClientTcpPort'] = windowResults.tcpPort;
    this.testSummary['end'] = this.helperService.getReportDateTimeString(resultsArr.end);
    this.testSummary['start'] = this.helperService.getReportDateTimeString(resultsArr.start);

    this.testSummary['engAIPAddr'] = windowResults.engAIPAddr;
    this.testSummary['engBPublicIPAddr'] = windowResults.engBPublicIPAddr;
    this.testSummary['engBPrivateIPAddr'] = windowResults.engBPrivateIPAddr;
    this.testSummary['engBType'] = windowResults.engBType;
    this.testSummary['tcpPort'] = windowResults.tcpPort;

    //Not needed for customer view
    this.windowSummary = this.windowWalkService.getWindowSummary(result, this.isAgentToJmep);
    const numWalks = this.configuration?.['numWindow'];
    this.windowSummary.forEach(item => {
      if (item.direction && !isNil(item.index)) {
        if (!this.isAgentToJmep) {
          if (!this.runSaturationWindow) {
            item.title = `${item.direction} Window Walk ${item.index} `;
          } else {
            if (item.index === numWalks + 1) {
              item.title = `${item.direction} Window Saturation Walk`;
            } else {
              item.title = `${item.direction} Window Walk ${item.index} `;
            }
          }
        } else {
          if (!this.runSaturationWindow) {
            item.title = `Throughput Window Walk ${item.index} Result`;
          } else {
            if (item.index === numWalks + 1) {
              item.title = 'Throughput Window Saturation Walk ';
            } else {
              item.title = `Throughput Window Walk ${item.index} Result`;
            }
          }
        }
      }
    });
    const testResults = this.getTruespeedTestResults();
    this.testResultsDataSource = new MatTableDataSource(testResults);
    if (
      (this.requestInfo['mode'] !== 'MA-TO-JMEP' && this.upstreamSummary['verdict'] === 'FAIL') ||
      this.downstreamSummary['verdict'] === 'FAIL'
    ) {
      this.overallVerdict = 'FAIL';
    } else {
      this.overallVerdict = 'PASS';
    }
    this.bulletChartOptions = this.transformService.CreateBulletChart(
      this.upstreamWindowResult.window,
      this.upstreamWindowResult.ideal,
      this.downstreamWindowResult.ideal,
      this.upstreamWindowResult.avg,
      this.downstreamWindowResult.avg,
      this.runSaturationWindow,
      this.dynamicSaturationWindow,
      this.testType
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getVerdictMessage(summary, noWarningMsg): any {
    const threshold = summary.upStreamThresholdVal
      ? summary.upStreamThresholdVal
      : summary.downStreamThresholdVal
      ? summary.downStreamThresholdVal
      : this.defaultThresholdValue;
    if (summary && summary.verdict === 'FAIL') {
      const diagMsg = this.getDiagnosisMessage(summary);
      if (diagMsg) {
        return ' - ' + diagMsg;
      }
      if (
        !isNaN(summary.bestAvg) &&
        !isNaN(summary.target) &&
        summary.target > 0 &&
        (summary.bestAvg / summary.target) * 100 < threshold
      ) {
        return (
          ' - The throughput is less than ' +
          this.getThresholdValueWithUnit(threshold, summary) +
          ' of the target.'
        );
      }
    } else {
      let msg = '';
      msg =
        ' - The throughput is more than ' +
        this.getThresholdValueWithUnit(threshold, summary) +
        ' of the target.';
      if (!noWarningMsg && summary.tcpLow) {
        const diagMsg = this.getDiagnosisMessage(summary);
        if (diagMsg) {
          msg += ' However, ' + diagMsg;
        }
      }
      return msg;
    }
  }

  getThresholdValueWithUnit(throughputThreshold: number, summary: any): string {
    throughputThreshold = throughputThreshold ?? this.defaultThresholdValue;
    return this.isThresholdUnitMbps
      ? summary?.direction === 'up'
        ? this.thresholdUpstream + 'Mbps'
        : this.thresholdDownstream + 'Mbps'
      : throughputThreshold + '%';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getDiagnosisMessage(summary): any {
    let msg = null;
    const threshold = summary.upStreamThresholdVal
      ? summary.upStreamThresholdVal
      : summary.downStreamThresholdVal
      ? summary.downStreamThresholdVal
      : this.defaultThresholdValue;
    if (summary.diagnosisMessage && summary.diagnosisMessage[0] && threshold) {
      summary.diagnosisMessage[0] = summary.diagnosisMessage[0].replace(
        '90%',
        this.getThresholdValueWithUnit(threshold, summary)
      );
    }
    if (summary.diagnosisMessage && summary.diagnosisMessage.length) {
      msg = summary.diagnosisMessage[0].split('b>')[1];
      if (msg && msg.length) {
        return msg.substring(0, msg.length - 2);
      }
    }
    return msg;
  }

  getDiagnosis(direction: string, diagnosis: string): Array<string> {
    let diagnosisList = [];
    if (diagnosis) {
      diagnosisList = diagnosis.split(',');
    }
    let diagnosisMap = this.upstreamDiagnosisMap;
    let messages = [];

    if (direction === 'downstream') {
      diagnosisMap = this.downstreamDiagnosisMap;
    }

    for (let i = 0; i < diagnosisList.length; i++) {
      if (diagnosisMap[diagnosisList[i]]) {
        messages = messages.concat(diagnosisMap[diagnosisList[i]]);
      }
    }

    return messages;
  }

  createDiagnosisMap(direction: string): { TCP_EFFICIENCY_LOW? } {
    const diagnosisMap = {};

    diagnosisMap['EFF_LOW_DELAY_LOW'] = [
      this.diagnosisMessage['SPECIAL1'],
      this.diagnosisMessage['SPECIAL3'],
      this.diagnosisMessage['CAUSE2'],
      this.diagnosisMessage['CAUSE3'],
      this.diagnosisMessage['CAUSE4'],
      this.diagnosisMessage['CAUSE5'],
      this.diagnosisMessage['CAUSE11'],
      this.diagnosisMessage['CAUSE1']
    ];

    diagnosisMap['EFF_LOW_DELAY_MED'] = [
      this.diagnosisMessage['SPECIAL1'],
      this.diagnosisMessage['SPECIAL3'],
      this.diagnosisMessage['CAUSE5'],
      this.diagnosisMessage['CAUSE2'],
      this.diagnosisMessage['CAUSE11'],
      this.diagnosisMessage['CAUSE3'],
      this.diagnosisMessage['CAUSE4'],
      this.diagnosisMessage['CAUSE1']
    ];

    diagnosisMap['EFF_HIGH_DELAY_LOW'] = [
      this.diagnosisMessage['SPECIAL1'],
      this.diagnosisMessage['SPECIAL5'],
      this.diagnosisMessage['CAUSE12'],
      this.diagnosisMessage['CAUSE13'],
      this.diagnosisMessage['CAUSE14']
    ];

    diagnosisMap['THRUPUT_LOW_MTU_SML'] = [
      this.diagnosisMessage['SPECIAL6'],
      this.diagnosisMessage['CAUSE10']
    ];

    diagnosisMap['POSSIBLE_PROXY_DETECTED'] = [this.diagnosisMessage['SPECIAL2']];

    diagnosisMap['NO_HIGH_ACCURACY_METRICS'] = [this.diagnosisMessage['SPECIAL8']];

    diagnosisMap['MTU_LESS_THAN_1400'] = [this.diagnosisMessage['SPECIAL7']];

    diagnosisMap['TCP_EFFICIENCY_LOW'] = [
      this.diagnosisMessage['SPECIAL3'],
      this.diagnosisMessage['CAUSE3'],
      this.diagnosisMessage['CAUSE4'],
      this.diagnosisMessage['CAUSE5'],
      this.diagnosisMessage['CAUSE2'],
      this.diagnosisMessage['CAUSE1']
    ];

    if (direction === 'upstream') {
      diagnosisMap['EFF_LOW_DELAY_HIGH'] = [
        this.diagnosisMessage['SPECIAL1'],
        this.diagnosisMessage['SPECIAL3'],
        this.diagnosisMessage['CAUSE5'],
        this.diagnosisMessage['CAUSE2'],
        this.diagnosisMessage['CAUSE3'],
        this.diagnosisMessage['CAUSE4'],
        this.diagnosisMessage['SPECIAL4'],
        this.diagnosisMessage['CAUSE6'],
        this.diagnosisMessage['CAUSE7_2'],
        this.diagnosisMessage['CAUSE8'],
        this.diagnosisMessage['CAUSE9'],
        this.diagnosisMessage['CAUSE11']
      ];

      diagnosisMap['EFF_HIGH_DELAY_HIGH'] = [
        this.diagnosisMessage['SPECIAL1'],
        this.diagnosisMessage['SPECIAL4'],
        this.diagnosisMessage['CAUSE6'],
        this.diagnosisMessage['CAUSE7_2'],
        this.diagnosisMessage['CAUSE8'],
        this.diagnosisMessage['CAUSE9'],
        this.diagnosisMessage['CAUSE11'],
        this.diagnosisMessage['CAUSE15']
      ];
    } else {
      diagnosisMap['EFF_LOW_DELAY_HIGH'] = [
        this.diagnosisMessage['SPECIAL1'],
        this.diagnosisMessage['SPECIAL3'],
        this.diagnosisMessage['CAUSE5'],
        this.diagnosisMessage['CAUSE2'],
        this.diagnosisMessage['CAUSE3'],
        this.diagnosisMessage['CAUSE4'],
        this.diagnosisMessage['SPECIAL4'],
        this.diagnosisMessage['CAUSE6'],
        this.diagnosisMessage['CAUSE7_1'],
        this.diagnosisMessage['CAUSE8'],
        this.diagnosisMessage['CAUSE9'],
        this.diagnosisMessage['CAUSE11']
      ];

      diagnosisMap['EFF_HIGH_DELAY_HIGH'] = [
        this.diagnosisMessage['SPECIAL1'],
        this.diagnosisMessage['SPECIAL4'],
        this.diagnosisMessage['CAUSE6'],
        this.diagnosisMessage['CAUSE7_1'],
        this.diagnosisMessage['CAUSE8'],
        this.diagnosisMessage['CAUSE9'],
        this.diagnosisMessage['CAUSE11'],
        this.diagnosisMessage['CAUSE15']
      ];
    }

    return diagnosisMap;
  }
  convertRate(rateType: string, rate: string): string {
    if (rateType && rate) {
      const mtu = parseFloat(this.testSummary['mtu']);
      const mss = parseFloat(this.testSummary['mss']);
      const rrr = parseFloat(rate);
      if (this.tagging === 'TAGGED') {
        this.l1Rate = (rrr * (mtu + 42)) / mss;
        this.l2Rate = this.l1Rate * ((mtu + 18) / (mtu + 42));
        this.l3Rate = this.l1Rate * (mtu / (mtu + 42));
      } else {
        this.l1Rate = (rrr * (mtu + 38)) / mss;
        this.l2Rate = this.l1Rate * ((mtu + 14) / (mtu + 38));
        this.l3Rate = this.l1Rate * (mtu / (mtu + 38));
      }

      switch (rateType) {
        case 'L1':
          return parseFloat(this.l1Rate).toFixed(2);
        case 'L2':
          return parseFloat(this.l2Rate).toFixed(2);
        case 'L3':
          return parseFloat(this.l3Rate).toFixed(2);
      }
    }
    return parseFloat(rate).toFixed(2);
  }

  thinClientConvertRate = function (rateType: string, rate: string): string {
    if (rateType && rate) {
      const mtu = 1500;
      const mss = 1460;
      const rrr = parseFloat(rate);
      if (this.tagging === 'TAGGED') {
        this.L1Rate = (rrr * (mtu + 42)) / mss;
        this.L2Rate = this.L1Rate * ((mtu + 18) / (mtu + 42));
        this.L3Rate = this.L1Rate * (mtu / (mtu + 42));
      } else {
        this.L1Rate = (rrr * (mtu + 38)) / mss;
        this.L2Rate = this.L1Rate * ((mtu + 14) / (mtu + 38));
        this.L3Rate = this.L1Rate * (mtu / (mtu + 38));
      }

      switch (rateType) {
        case 'L1':
          return parseFloat(this.L1Rate).toFixed(2);
        case 'L2':
          return parseFloat(this.L2Rate).toFixed(2);
        case 'L3':
          return parseFloat(this.L3Rate).toFixed(2);
      }
    }
    return parseFloat(rate).toFixed(2);
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.expertView) {
      this.expertView = changes.expertView.currentValue;
    }
    if (changes && (changes.runId || changes.reportStartTime)) {
      this.requestInfo = {};
      this.upstreamSummary = {};
      this.downstreamSummary = {};
      this.testSummary = {};
      this.clientInfo = {};
      this.windowResults = [];
      this.windowSummary = [];
      this.configuration = {};
      this.upstreamWindowResult = null;
      this.downstreamWindowResult = null;
      this.testType = null;
      this.isThinClientTest = false;
      this.init();
    }
  }

  //eslint-disable-next-line
  processClientInfo(response: any): any {
    let interfaceInfo = false;
    let systemInfo = false;
    let processInfo = false;
    const reports = response;

    let lastCpuUsageSequence = 0;
    let lastCpuUsageReport = [];

    for (let i = 0; i < reports.length; i++) {
      if (reports[i]['ietf-lmap-report:report'] && reports[i]['ietf-lmap-report:report'].result) {
        const result = reports[i]['ietf-lmap-report:report'].result[0];
        if (result.tag && result.tag.length > 0) {
          const values = result.table[0].row;
          let sequence = null;
          let tableType = null;
          let foundSeq = false;
          let foundType = false;
          for (let x = 0, len = result.tag.length; x < len; ++x) {
            if (result.tag[x].indexOf('sequence:') >= 0) {
              sequence = parseInt(result.tag[x].split(':')[1]);
              foundSeq = true;
            }
            if (result.tag[x].indexOf('table-type:') >= 0) {
              tableType = result.tag[x];
              foundType = true;
            }

            if (foundSeq && foundType) {
              break;
            }
          }

          //interface info
          if (tableType && tableType.indexOf('interfaces') !== -1 && !interfaceInfo) {
            this.hasClientInfo = true;
            interfaceInfo = true;
            this.clientInfo['interfaces'] = [];

            for (let j = 0; j < values.length; j++) {
              const d = {};
              d['name'] = values[j].value[0];
              d['ip'] = values[j].value[1];
              d['transmitBuffer'] = values[j].value[2] ? values[j].value[2] : null;
              d['receiveBuffer'] = values[j].value[3] ? values[j].value[3] : null;
              this.clientInfo['interfaces'].push(d);
            }
          }

          //system info
          if (tableType && tableType.indexOf('system-info') !== -1 && !systemInfo) {
            this.hasClientInfo = true;
            systemInfo = true;
            this.clientInfo['systemInfo'] = {};
            this.clientInfo['systemInfo'].engBPublicIPAddr = this.testSummary['engBPublicIPAddr'];
            this.clientInfo['systemInfo'].engBPrivateIPAddr = this.testSummary['engBPrivateIPAddr'];
            this.clientInfo['systemInfo'].cpuNumber = values[0].value[0];
            this.clientInfo['systemInfo'].cpuModel = values[0].value[1];
            this.clientInfo['systemInfo'].cpuVendor = values[0].value[2];
            this.clientInfo['systemInfo'].osVersion = values[0].value[3];
            this.clientInfo['systemInfo'].memory = values[0].value[7];
            if (this.isThinClientTest) {
              if (values[0].value[0] === 'thin-engine') {
                this.clientInfo['systemInfo'].engBPublicIPAddr =
                  this.testSummary['engBPublicIPAddr'];
                this.clientInfo['systemInfo'].engBPrivateIPAddr =
                  this.testSummary['engBPrivateIPAddr'];
                this.clientInfo['systemInfo'].deviceOS = values[0].value[1];
                this.clientInfo['systemInfo'].browser = values[0].value[2];
                this.clientInfo['systemInfo'].browserVersion = values[0].value[3];
                this.clientInfo['systemInfo'].deviceType = values[0].value[4];
                this.clientInfo['systemInfo'].latitude = values[0].value[5];
                this.clientInfo['systemInfo'].longitude = values[0].value[6];
              }
            }
          }

          //processes
          if (tableType && tableType.indexOf('processes') !== -1 && !processInfo) {
            this.hasClientInfo = true;
            processInfo = true;
            this.clientInfo['processes'] = {};
            for (let j = 0; j < values.length; j++) {
              const name = values[j].value[0];
              if (this.clientInfo['processes'][name]) {
                this.clientInfo['processes'][name] += 1;
              } else {
                this.clientInfo['processes'][name] = 1;
              }
            }
          }
          //cpu usage
          if (tableType && tableType.indexOf('cpu-usage') !== -1) {
            this.hasClientInfo = true;

            if (sequence && lastCpuUsageSequence < sequence) {
              lastCpuUsageSequence = sequence;
              lastCpuUsageReport = values;
            }
          }
        }
      }
    }

    if (this.hasClientInfo) {
      let maxCpuUsage = 0;
      for (let i = 0; i < lastCpuUsageReport.length; i++) {
        this.clientCpuUsage.push(parseFloat(lastCpuUsageReport[i].value[0]) * 100);
        const usedMemory = this.clientInfo['systemInfo'].memory - lastCpuUsageReport[i].value[1];
        const usedPercet = (usedMemory / this.clientInfo['systemInfo'].memory) * 100;
        this.clientMemoryUsage.push(usedPercet);
        if (parseFloat(lastCpuUsageReport[i].value[0]) > maxCpuUsage) {
          maxCpuUsage = parseFloat(lastCpuUsageReport[i].value[0]);
        }
      }
      this.clientInfo['systemInfo'].maxCpuUsage = (maxCpuUsage * 100).toFixed(2);
    }
    if (this.hasClientInfo) {
      const clientInfoConfiguration = this.transformService.getClientConfiguration(
        this.clientInfo,
        this.testType
      );
      this.clientInfoDataSource = new MatTableDataSource(clientInfoConfiguration);
    }
  }

  getTruespeedTestResults(): Array<TableData> {
    const isDynamicSaturationEnabled = JSON.parse(
      this.configuration['dynamicSaturation'] || 'false'
    );
    this.truespeedTestResults = [];
    this.truespeedTestResults.push({
      header: 'Engine A IP',
      value: this.testSummary['engAIPAddr'],
      hide: !this.testSummary['engAIPAddr']
    });
    this.truespeedTestResults.push({
      header: this.testType === TestMode.CLIENT_TO_MA ? 'Client Public IP' : 'Engine B Public IP',
      value: this.testSummary['engBPublicIPAddr'],
      hide: !this.testSummary['engBPublicIPAddr']
    });
    if (this.testSummary['engBPrivateIPAddr']) {
      this.truespeedTestResults.push({
        header:
          this.testType === TestMode.CLIENT_TO_MA ? 'Client Private IP' : 'Engine B Private IP',
        value: this.testSummary['engBPrivateIPAddr'],
        hide: !this.testSummary['engBPrivateIPAddr']
      });
    }
    if (this.testSummary['tcpPort']) {
      this.truespeedTestResults.push({
        header: 'TCP Port',
        value: this.testSummary['tcpPort']
      });
    }
    this.truespeedTestResults.push(
      {
        header: 'MSS',
        value: this.testSummary['mss'] + ' bytes'
      },
      {
        header: 'MTU',
        value: this.testSummary['mtu'] + ' bytes'
      },
      {
        header: 'RTT Base',
        value: this.testSummary['rttBase'] + ' ms'
      },
      {
        header: 'Max Upstream Avg Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(this.upstreamSummary['bestAvg'])}
        </table>`
      },
      {
        header: 'Max Upstream Peak Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(this.upstreamSummary['bestPeak'])}
        </table>`
      },
      {
        header: 'Max TCP Upstream Window Size',
        value: this.upstreamSummary['bestWindow'],
        hide: isDynamicSaturationEnabled
      },
      {
        header: 'Start TCP Upstream Window Size',
        value: this.upstreamSummary['bestWindow'],
        hide: !isDynamicSaturationEnabled
      },
      {
        header: 'Dynamic Saturation Upstream Window Size',
        value: this.upstreamSummary['dynamicSatWin'],
        hide: !JSON.parse(this.configuration['dynamicSaturation'] || 'false')
      },
      {
        header: 'Total TCP Upstream Window Size',
        value: this.upstreamSummary['totalWinString'],
        hide: !isDynamicSaturationEnabled
      },
      {
        header: this.isAgentToJmep ? 'Max Avg Throughput' : 'Max Downstream Avg Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(this.downstreamSummary['bestAvg'])}
        </table>`
      },
      {
        header: this.isAgentToJmep ? 'Max Peak Throughput' : 'Max Downstream Peak Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(this.downstreamSummary['bestPeak'])}
        </table>`
      },
      {
        header: 'Start TCP Downstream Window Size',
        value: this.downstreamSummary['bestWindow'],
        hide: !isDynamicSaturationEnabled
      },
      {
        header: this.isAgentToJmep
          ? 'Max TCP Throughput Window Size'
          : 'Max TCP Downstream Window Size',
        value: this.downstreamSummary['bestWindow'],
        hide: isDynamicSaturationEnabled
      },
      {
        header: this.isAgentToJmep
          ? 'Dynamic Saturation Throughput Window Size'
          : 'Dynamic Saturation Downstream Window Size',
        value: this.downstreamSummary['dynamicSatWin'],
        hide: !JSON.parse(this.configuration['dynamicSaturation'] || 'false')
      },
      {
        header: this.isAgentToJmep
          ? 'Total TCP Throughput Window Size'
          : 'Total TCP Downstream Window Size',
        value: this.downstreamSummary['totalWinString'],
        hide: !JSON.parse(this.configuration['dynamicSaturation'] || 'false')
      }
    );
    if (this.isAgentToJmep) {
      this.truespeedTestResults = this.truespeedTestResults.filter(
        item => item.header.indexOf('Upstream') === -1
      );
    }
    return this.truespeedTestResults;
  }
  //eslint-disable-next-line
  getwindowResultDataSource(windowResult: any): MatTableDataSource<TableData> {
    const configuration = [
      {
        header: 'Window Size Per Connection',
        value: windowResult['window-size'],
        hide: windowResult?.['dynamicSaturation'] === true
      },
      {
        header: 'Connections',
        value: windowResult['connections']
      },
      {
        header: 'Aggregate Window',
        value: windowResult['aggregate-window']
      },
      {
        header: 'Actual Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(windowResult['avg-thruput'])}
        </table>`
      },
      {
        header: 'Ideal Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(windowResult['rate-under-test'])}
        </table>`
      },
      {
        header: 'Target Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(windowResult['ideal-thruput'])}
        </table>`
      },
      { header: 'Buffer Delay', value: windowResult['buffer-delay'] + ' %' },
      {
        header: 'TCP Efficiency',
        value: windowResult['tcp-efficiency'] + ' %'
      },
      {
        header: 'Total Retransmits',
        value:
          windowResult['total-retransmit'] !== null &&
          windowResult['total-retransmit'] !== undefined &&
          windowResult['total-retransmit'] >= 0
            ? windowResult['total-retransmit'].toLocaleString('en')
            : 'Data no longer available'
      }
    ];
    return new MatTableDataSource(configuration);
  }

  //eslint-disable-next-line
  getThinClientwindowResultDataSource(windowResult: any): MatTableDataSource<TableData> {
    const configuration = [
      {
        header: 'Actual Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThinClientThroughputConvertedData(windowResult['avg-thruput'])}
        </table>`
      },
      {
        header: 'Target Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThinClientThroughputConvertedData(windowResult['ideal-thruput'])}
        </table>`
      }
    ];
    return new MatTableDataSource(configuration);
  }

  getThroughputConvertedData(rate: string): string {
    return `<tr>
    <td class="throughput-inner-table-data">${this.convertRate('L1', rate)} Mbps </td>
    <td class="throughput-inner-table-data">${this.convertRate('L2', rate)} Mbps </td>
    <td class="throughput-inner-table-data">${this.convertRate('L3', rate)} Mbps</td>
    <td class="throughput-inner-table-data">${this.convertRate('L4', rate)} Mbps</td>
    </tr>`;
  }

  getThinClientThroughputConvertedData(rate: string): string {
    return `<tr>
    <td class="throughput-inner-table-data">${this.thinClientConvertRate('L1', rate)} Mbps </td>
    <td class="throughput-inner-table-data">${this.thinClientConvertRate('L2', rate)} Mbps </td>
    <td class="throughput-inner-table-data">${this.thinClientConvertRate('L3', rate)} Mbps</td>
    <td class="throughput-inner-table-data">${this.thinClientConvertRate('L4', rate)} Mbps</td>
    </tr>`;
  }

  calculateTotalStat(): void {
    if (this.windowSummary.length === this.windowResults.length) {
      for (const windowResult of this.windowResults) {
        const totalTransmit = windowResult?.transmitted.reduce((sum, val) => sum + val, 0);
        const totalRetransmit = windowResult?.retransmitted.reduce((sum, val) => sum + val, 0);

        const summary = this.windowSummary.find(item => item['window-id'] === windowResult.id);
        if (summary) {
          summary['total-transmit'] = totalTransmit;
          summary['total-retransmit'] = totalRetransmit;
        }
      }
    } else {
      this.calculateTimeoutId = setTimeout(() => this.calculateTotalStat(), 1000);
    }
  }

  getAgentURLLabel(): string {
    return this.overallConfiguration &&
      this.agentModelCheck &&
      this.agentModelCheck?.deviceData?.model?.model === AGENT_MODEL.VTA_TS
      ? 'vTA-TS Test URL'
      : 'vSPA-SAT-TS Test URL';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getMatchedWindowResult(windowId: string): any {
    return this.windowResults.find(result => result['id'] === windowId);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.rtResultTimeoutId) {
      clearTimeout(this.rtResultTimeoutId);
    }
    if (this.calculateTimeoutId) {
      clearTimeout(this.calculateTimeoutId);
    }
  }
}
